/**
 * Brand Ambassadors page components.
 *
 * <ww-creator-wall>
 *   Desktop (>= 990px): five columns drift on their own sine wave.
 *   Mobile: a pinned two-row scroller tethered to page scroll.
 *   Also owns reel playback (play in view, pause out of view) and the
 *   one-reel-unmuted-at-a-time rule.
 *
 * <ww-reveal>
 *   Adds .is-revealed to [data-ww-item] children once they enter the
 *   viewport; CSS runs the roll-in / stack-in keyframes.
 *
 * <ww-apply-modal>
 *   Extends the theme's ModalDialog (assets/theme.js) so the application form
 *   opens from any ww-button rendered with a `modal` target. Reopens itself
 *   after Shopify's post-submit redirect so the result message is seen, and in
 *   the Theme Editor when its section or a block inside it is selected.
 *
 * <ww-form-steps>
 *   Turns the fieldsets inside a contact form into a wizard: one step visible
 *   at a time, each validated with the browser's own constraint validation
 *   before advancing. Optionally mirrors the submission to a webhook.
 *
 * Every ww-* section includes this file with a deferred <script>, so it can
 * execute more than once per page: the customElements.get guards are required.
 * The 990px breakpoint must match assets/ww-ambassadors.css.
 */

if (!customElements.get('ww-creator-wall')) {
  class WWCreatorWall extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.ww-wall') || this;
      this.columns = Array.from(this.querySelectorAll('[data-ww-col]'));
      this.rowTop = this.querySelector('[data-ww-row="top"]');
      this.rowBottom = this.querySelector('[data-ww-row="bot"]');
      this.scrollCue = this.querySelector('[data-ww-scroll-cue]');
      this.scrollIcon = this.querySelector('[data-ww-scroll-icon]');
      this.videos = Array.from(this.querySelectorAll('video'));
      this.travel = parseFloat(this.dataset.travel) || 360;
      this.autoplay = this.dataset.autoplay !== 'false';
      this.preloadReels = this.dataset.preload !== 'false';
      // With scroll-driven animations the CSS moves the mobile rows (see
      // @supports block in ww-ambassadors.css); the JS tether is the fallback.
      // iOS WebKit's compositor timeline settles backward a few pixels when
      // touch momentum ends, so use the deterministic JS tether there.
      const supportsScrollTimeline = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('animation-timeline', 'scroll()');
      const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      this.cssScrollTimeline = supportsScrollTimeline && !isIOS;
      if (supportsScrollTimeline && isIOS) {
        if (this.rowTop) this.rowTop.style.animation = 'none';
        if (this.rowBottom) this.rowBottom.style.animation = 'none';
      }
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.columnOffsets = new Map();
      this.inView = false;
      this.frozen = false;
      // a11y: the wall moves on its own, so it carries a pause control
      // (WCAG 2.2.2). Under reduced motion it starts paused: reels do not
      // autoplay until the visitor presses play.
      this.pauseButton = this.querySelector('[data-ww-pause]');
      this.userPaused = this.reduceMotionQuery.matches;
      this.focusHeld = false;
      this.visibleVideos = new Set();
      this.mobileViewportHeight = null;
      this.mobileViewportWidth = null;
      this.lastScrollY = null;
      this.lastWidth = null;

      this.onFrame = this.onFrame.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onLayoutChange = this.onLayoutChange.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);

      if ('IntersectionObserver' in window) {
        // The animation loop only does work while the wall is near the viewport.
        this.stageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
          });
        }, { rootMargin: '10% 0px 10% 0px' });
        this.stageObserver.observe(this.root);

        // Reels play only while a quarter of them is on screen. Elements in the
        // hidden layout (display: none) never intersect, so they never load.
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.toggleVideo(entry.target, entry.isIntersecting));
        }, { threshold: 0.25 });
        this.videos.forEach((video) => this.videoObserver.observe(video));
      } else {
        this.inView = true;
        this.videos.forEach((video) => this.toggleVideo(video, true));
      }

      this.querySelectorAll('.ww-reel__mute').forEach((button) => {
        button.addEventListener('click', () => this.onMuteToggle(button));
      });
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
        this.pauseButton.addEventListener('click', () => this.setPaused(!this.userPaused));
      }
      // Keyboard focus on a card or mute button that has drifted (desktop) or
      // swept (mobile) out of view brings it back into view.
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.addMediaListener(this.desktopQuery, this.onLayoutChange);
      window.addEventListener('resize', this.onResize);
      if ('ResizeObserver' in window) {
        // Re-measure when the card height changes (display font loading) or
        // when anything above the wall changes height (the pinned scroll
        // range is expressed in document offsets).
        this.resizeObserver = new ResizeObserver(() => this.positionStage());
        if (this.rowBottom) this.resizeObserver.observe(this.rowBottom);
        this.resizeObserver.observe(document.body);
      }
      this.positionStage();
      this.scheduleWarm();

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.frame = requestAnimationFrame(this.onFrame);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      if (this.stageObserver) this.stageObserver.disconnect();
      if (this.videoObserver) this.videoObserver.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.removeMediaListener(this.desktopQuery, this.onLayoutChange);
      window.removeEventListener('resize', this.onResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.viewportProbe) this.viewportProbe.remove();
      clearTimeout(this.warmTimer);
      window.removeEventListener('load', this.onLoad);
      this.warmQueue = [];
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.videos.forEach((video) => video.pause());
    }

    addMediaListener(query, handler) {
      if (query.addEventListener) query.addEventListener('change', handler);
      else query.addListener(handler);
    }

    removeMediaListener(query, handler) {
      if (query.removeEventListener) query.removeEventListener('change', handler);
      else query.removeListener(handler);
    }

    get isDesktop() {
      return this.desktopQuery.matches;
    }

    onFrame(now) {
      if (this.inView) {
        if (this.isDesktop) {
          if (!this.frozen && !this.userPaused && !this.focusHeld && !this.reduceMotionQuery.matches) this.drift(now / 1000);
        } else {
          if (!this.cssScrollTimeline) this.tether();
          this.updateScrollCue();
        }
      }
      this.frame = requestAnimationFrame(this.onFrame);
    }

    // Each column bobs on its own sine wave. data-ww-col holds the direction
    // and relative speed (negative = starts downwards). Formula from the design.
    drift(seconds) {
      this.columns.forEach((column) => {
        const direction = parseFloat(column.dataset.wwCol) || 1;
        const offset = Math.sin(seconds * 0.22 * Math.abs(direction)) * (this.travel / 2) * Math.sign(direction);
        this.setColumnOffset(column, offset);
      });
    }

    setColumnOffset(column, offset) {
      this.columnOffsets.set(column, offset);
      column.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    }

    // Mobile: progress through the tall section drives the two rows in
    // opposite directions so reel i meets card i at the viewport centre
    // (see the pitch note in ww-ambassadors.css).
    tether() {
      if (!this.rowTop || !this.rowBottom) return;
      const wrapper = this.rowTop.parentElement;
      const scrollY = window.scrollY;
      if (scrollY === this.lastScrollY && wrapper.clientWidth === this.lastWidth) return;
      this.lastScrollY = scrollY;
      this.lastWidth = wrapper.clientWidth;

      const progress = this.scrollProgress();
      const distance = Math.max(0, this.rowTop.scrollWidth - wrapper.clientWidth);
      this.rowTop.style.transform = `translate3d(${(-progress * distance).toFixed(2)}px, 0, 0)`;
      this.rowBottom.style.transform = `translate3d(${(-(1 - progress) * distance).toFixed(2)}px, var(--ww-row-shift, 0px), 0)`;
    }

    // 0 when the stage first sticks, 1 when the section's bottom edge
    // reaches the viewport. The stage remains sticky through the small
    // bottom gutter after that, holding the completed rows in place while
    // the following section enters.
    scrollProgress() {
      const total = Math.max(1, this.pinDistance || (this.root.offsetHeight - this.offsetHeight));
      const offset = this.getBoundingClientRect().top - this.root.getBoundingClientRect().top;
      return Math.min(1, Math.max(0, offset / total));
    }

    // Rotate with the horizontal sweep, then fade while the completed wall
    // releases and the following section begins entering the viewport.
    updateScrollCue() {
      if (!this.scrollCue || !this.scrollIcon) return;
      const offset = this.getBoundingClientRect().top - this.root.getBoundingClientRect().top;
      const progress = this.scrollProgress();
      const exitDistance = Math.max(1, this.root.offsetHeight - this.offsetHeight - this.pinDistance);
      const exitProgress = Math.min(1, Math.max(0, (offset - this.pinDistance) / exitDistance));
      const rotation = this.reduceMotionQuery.matches ? 0 : progress * 720;
      const hidden = exitProgress >= 0.99;
      this.scrollIcon.style.transform = `rotate(${rotation.toFixed(2)}deg)`;
      this.scrollCue.style.opacity = hidden ? '0' : (0.7 * (1 - exitProgress)).toFixed(3);
      this.scrollCue.style.visibility = hidden ? 'hidden' : 'visible';
    }

    // Height of the small viewport (CSS 100svh) in px, measured through a
    // hidden probe. Cache it until the viewport width changes: iOS Safari
    // fires resize events as its toolbars collapse and can otherwise change
    // reel and stage heights in the middle of the pinned sweep.
    smallViewportHeight() {
      const viewportWidth = Math.round(document.documentElement.clientWidth || window.innerWidth);
      if (this.mobileViewportHeight && this.mobileViewportWidth === viewportWidth) {
        return this.mobileViewportHeight;
      }
      if (!this.viewportProbe) {
        this.viewportProbe = document.createElement('div');
        this.viewportProbe.setAttribute('aria-hidden', 'true');
        this.viewportProbe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100svh;visibility:hidden;pointer-events:none;';
        document.body.appendChild(this.viewportProbe);
      }
      this.mobileViewportWidth = viewportWidth;
      this.mobileViewportHeight = this.viewportProbe.offsetHeight || window.innerHeight;
      return this.mobileViewportHeight;
    }

    // The next section's wave is pulled upward over this section. Reserve
    // that overlap below the cards so the wave keeps the normal stage gap
    // instead of colliding with the final card as the sticky stage releases.
    exitOverlap() {
      const section = this.root.closest('.shopify-section') || this.root.parentElement;
      const nextSection = section && section.nextElementSibling;
      const wave = nextSection && nextSection.querySelector('.ww-wave');
      if (!wave) return 0;
      return Math.max(0, -(parseFloat(getComputedStyle(wave).marginTop) || 0));
    }

    // Mobile only: centre the content-height stage in the small viewport via
    // its sticky offset, hand back the card overlap in layout (--ww-row-h),
    // keep the pinned sweep at 160svh of scroll whatever the stage height,
    // and hold the finished rows until the following section reaches the
    // bottom of the viewport.
    positionStage() {
      if (this.isDesktop) {
        this.pinDistance = null;
        this.style.top = '';
        this.style.removeProperty('--ww-row-h');
        this.style.removeProperty('--ww-mobile-vh');
        this.style.removeProperty('--ww-exit-overlap');
        this.style.removeProperty('--ww-pin-start');
        this.style.removeProperty('--ww-pin-end');
        this.root.style.height = '';
        return;
      }
      const viewportHeight = this.smallViewportHeight();
      this.style.setProperty('--ww-mobile-vh', `${viewportHeight}px`);
      this.style.setProperty('--ww-exit-overlap', `${this.exitOverlap()}px`);
      if (this.rowBottom) this.style.setProperty('--ww-row-h', `${this.rowBottom.offsetHeight}px`);
      const stageHeight = this.offsetHeight;
      const free = viewportHeight - stageHeight;
      const stickyTop = Math.max(0, Math.round(free / 2));
      const bottomGutter = Math.max(0, viewportHeight - stickyTop - stageHeight);
      this.pinDistance = Math.max(1, Math.round(viewportHeight * 1.6));
      this.style.top = `${stickyTop}px`;
      this.root.style.height = `${stageHeight + this.pinDistance + bottomGutter}px`;
      // Pinned range as document scroll offsets, for the CSS scroll timeline.
      const pinStart = this.root.getBoundingClientRect().top + window.scrollY - stickyTop;
      const pinEnd = pinStart + this.pinDistance;
      this.style.setProperty('--ww-pin-start', `${Math.round(pinStart)}px`);
      this.style.setProperty('--ww-pin-end', `${Math.round(pinEnd)}px`);
      this.lastScrollY = null;
    }

    // Reels are preload="none" in the HTML so hidden copies and no-JS visits
    // cost nothing. After the page has loaded, warm the active layout's reels
    // one at a time (in sweep order) so they play instantly when reached.
    // Duplicate desktop tiles share a URL and are served from the HTTP cache.
    scheduleWarm() {
      if (!this.preloadReels) return;
      const connection = navigator.connection;
      if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
      const start = () => {
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 800));
        idle(() => this.warmVideos(), { timeout: 2500 });
      };
      if (document.readyState === 'complete') {
        start();
      } else {
        this.onLoad = start;
        window.addEventListener('load', this.onLoad, { once: true });
      }
    }

    activeVideos() {
      const scope = this.isDesktop ? '.ww-wall__columns' : '.ww-wall__rows';
      return Array.from(this.querySelectorAll(`${scope} video`));
    }

    warmVideos() {
      const seen = new Set();
      this.warmQueue = this.activeVideos().filter((video) => {
        const src = video.currentSrc || (video.querySelector('source') || {}).src || '';
        if (seen.has(src)) return false;
        seen.add(src);
        return true;
      });
      this.warmNext();
    }

    warmNext() {
      const video = this.warmQueue.shift();
      if (!video) return;
      // Already fetching or playing (the observer started it) - move on.
      if (video.readyState > 0 || video.preload !== 'none' || !video.paused) {
        this.warmNext();
        return;
      }
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(this.warmTimer);
        video.removeEventListener('canplaythrough', done);
        video.removeEventListener('error', done);
        this.warmNext();
      };
      video.addEventListener('canplaythrough', done);
      video.addEventListener('error', done);
      this.warmTimer = setTimeout(done, 10000);
      video.preload = 'auto';
      video.load();
    }

    onResize() {
      this.positionStage();
    }

    toggleVideo(video, visible) {
      if (visible) this.visibleVideos.add(video);
      else this.visibleVideos.delete(video);
      if (visible) {
        if (this.autoplay && !this.userPaused && video.paused) this.play(video);
      } else if (!video.paused) {
        video.pause();
      }
    }

    // The wall's pause control: stops every reel and the desktop drift, and
    // on resume restarts only the reels that are on screen.
    setPaused(paused) {
      this.userPaused = paused;
      if (this.pauseButton) this.pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
      if (paused) {
        this.videos.forEach((video) => video.pause());
      } else {
        this.visibleVideos.forEach((video) => this.play(video));
      }
    }

    onFocusIn(event) {
      const block = event.target.closest('[data-ww-block]');
      if (!block) return;
      if (this.isDesktop) {
        // Hold the drift while focus is inside so the focused item stays put.
        this.focusHeld = true;
        this.centerDesktopBlock(block);
      } else {
        this.centerMobileBlock(block.dataset.wwBlock);
      }
    }

    onFocusOut(event) {
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.focusHeld = false;
    }

    play(video) {
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    }

    // Unmuting one reel mutes every other reel (in both layouts) so only one
    // audio track plays at a time.
    onMuteToggle(button) {
      const reel = button.closest('.ww-reel');
      const video = reel ? reel.querySelector('video') : null;
      if (!video) return;
      const unmuting = video.muted;
      if (unmuting) {
        this.videos.forEach((other) => {
          if (other !== video && !other.muted) {
            other.muted = true;
            this.syncMuteButton(other);
          }
        });
      }
      video.muted = !unmuting;
      if (video.paused) this.play(video);
      this.syncMuteButton(video);
    }

    syncMuteButton(video) {
      const reel = video.closest('.ww-reel');
      const button = reel ? reel.querySelector('.ww-reel__mute') : null;
      if (!button) return;
      button.classList.toggle('is-muted', video.muted);
      button.classList.toggle('is-unmuted', !video.muted);
      // The name stays fixed ("Sound, @handle"); aria-pressed carries the state.
      button.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
    }

    onVisibilityChange() {
      if (document.hidden) this.videos.forEach((video) => video.pause());
    }

    // When the layout flips at 990px, clear transforms so the tree that just
    // became visible starts from a neutral position. Playback is re-evaluated
    // by the IntersectionObserver on its own.
    onLayoutChange() {
      this.columns.forEach((column) => this.setColumnOffset(column, 0));
      if (this.rowTop) this.rowTop.style.transform = '';
      if (this.rowBottom) this.rowBottom.style.transform = '';
      this.lastScrollY = null;
      this.lastWidth = null;
      this.positionStage();
    }

    // Theme editor: bring the selected creator into view and hold the drift
    // until the block is deselected.
    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      this.frozen = true;
      if (this.isDesktop) {
        this.centerDesktopBlock(event.target);
      } else {
        this.centerMobileBlock(event.detail && event.detail.blockId);
      }
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.frozen = false;
    }

    centerDesktopBlock(target) {
      const column = target.closest('[data-ww-col]');
      if (!column) return;
      this.root.scrollIntoView({ block: 'start', behavior: 'smooth' });
      const stageRect = this.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const delta = (stageRect.top + stageRect.height / 2) - (targetRect.top + targetRect.height / 2);
      const current = this.columnOffsets.get(column) || 0;
      this.setColumnOffset(column, current + delta);
    }

    centerMobileBlock(blockId) {
      if (!this.rowTop || !blockId) return;
      const reels = Array.from(this.rowTop.querySelectorAll('.ww-reel'));
      const index = reels.findIndex((reel) => reel.dataset.wwBlock === blockId);
      if (index < 0) return;
      const progress = reels.length > 1 ? index / (reels.length - 1) : 0;
      const stickyTop = parseFloat(this.style.top) || 0;
      const sectionTop = this.root.getBoundingClientRect().top + window.scrollY;
      const travel = Math.max(0, this.pinDistance || (this.root.offsetHeight - this.offsetHeight));
      window.scrollTo({ top: sectionTop - stickyTop + progress * travel, behavior: 'smooth' });
    }
  }

  customElements.define('ww-creator-wall', WWCreatorWall);
}

if (!customElements.get('ww-reveal')) {
  class WWReveal extends HTMLElement {
    connectedCallback() {
      const items = Array.from(this.querySelectorAll('[data-ww-item]'));
      const threshold = parseFloat(this.getAttribute('threshold'));

      if (!('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-revealed'));
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          this.observer.unobserve(entry.target);
        });
      }, { threshold: Number.isFinite(threshold) ? threshold : 0.35 });

      items.forEach((item) => this.observer.observe(item));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('ww-reveal', WWReveal);
}

// ModalDialog is a top-level class in theme.js (a global lexical binding, not
// a window property), and theme.js runs earlier in the deferred script chain.
if (!customElements.get('ww-apply-modal') && typeof ModalDialog !== 'undefined') {
  class WWApplyModal extends ModalDialog {
    constructor() {
      // ModalDialog wires the ModalClose-* button, overlay click, Escape,
      // body scroll lock and focus trapping.
      super();

      // The confirmation screen's Close button.
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-modal-close]')) this.hide();
      });

      // a11y: a local Tab trap that measures the visible controls on every
      // keypress. The wizard hides and shows steps and buttons while open, so
      // edges captured when the modal opened go stale. Listening on the modal
      // itself means it only ever runs while focus is inside it.
      this.addEventListener('keydown', this.onTrapKeydown.bind(this));

      if (window.Shopify && window.Shopify.designMode) {
        const isThisSection = (event) => event.detail.sectionId === this.dataset.sectionId;

        document.addEventListener('shopify:section:select', (event) => {
          if (isThisSection(event)) this.show();
        });
        document.addEventListener('shopify:block:select', (event) => {
          if (isThisSection(event)) this.show();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (isThisSection(event)) this.hide();
        });
      }
    }

    connectedCallback() {
      super.connectedCallback();

      // Shopify reloads the page after a contact form post. Liquid marks the
      // confirmation (or a server error) with data-form-status; as a fallback,
      // any contact_posted flag in the URL also counts as success, because
      // Shopify can garble the flag's value when the return URL already has a
      // query string.
      const posted = /[?&]contact_posted=/.test(window.location.search);
      const status = this.querySelector('[data-form-status]');
      if (posted && !status) this.showConfirmation();
      if (posted || status) this.show();
    }

    show(opener) {
      super.show(opener);
      this.ensureFocusInside();
      if (opener) return;
      // Reopened by the page itself (post-submit reload, Theme Editor): there
      // is no button to come back to, so move focus to what changed rather
      // than to the close button. Runs after ModalDialog's own 300ms focus.
      setTimeout(() => {
        if (!this.hasAttribute('open')) return;
        const success = this.querySelector('[data-form-success]:not([hidden])');
        const invalid = this.querySelector('[aria-invalid="true"]');
        const target = success || invalid;
        if (target && typeof trapFocus === 'function') trapFocus(this, target);
      }, 320);
    }

    hide() {
      // With no opener (the modal opened itself), return focus to the first
      // visible button that opens it instead of dropping it on <body>.
      if (this.hasAttribute('open') && (!this.openedBy || !document.contains(this.openedBy))) {
        const openers = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
        this.openedBy = openers.find((button) => button.getClientRects().length > 0) || openers[0];
      }
      super.hide();
    }

    // On the first open after page load the dialog is still fading in when ModalDialog's 300ms
    // focus runs, and a not-yet-visible element can't take focus — so focus stayed on the opener
    // and Tab walked the page behind. Retry until focus is really inside, and while open pull any
    // focus that escapes back in.
    ensureFocusInside() {
      const panel = this.querySelector('[role="dialog"]') || this;
      let tries = 0;
      const attempt = () => {
        if (!this.hasAttribute('open') || this.contains(document.activeElement)) return;
        const target = this.focusableElements()[0];
        if (target) target.focus();
        if (!this.contains(document.activeElement) && ++tries < 30) setTimeout(attempt, 50);
      };
      setTimeout(attempt, 350);
      if (!this.onOutsideFocus) {
        this.onOutsideFocus = (event) => {
          if (!this.hasAttribute('open') || this.hasAttribute('closing') || this.contains(event.target)) return;
          const target = this.focusableElements()[0];
          if (target) target.focus();
        };
        document.addEventListener('focusin', this.onOutsideFocus);
      }
      panel.setAttribute('aria-modal', 'true');
    }

    focusableElements() {
      const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(this.querySelectorAll(selector)).filter((element) => (
        element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden'
      ));
    }

    onTrapKeydown(event) {
      if (event.key !== 'Tab' || !this.hasAttribute('open')) return;
      const elements = this.focusableElements();
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;
      const panel = this.querySelector('[role="dialog"]');
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    showConfirmation() {
      const confirmation = this.querySelector('[data-form-success]');
      const wizard = this.querySelector('ww-form-steps');
      if (confirmation) confirmation.hidden = false;
      if (wizard) wizard.hidden = true;
    }
  }

  customElements.define('ww-apply-modal', WWApplyModal);
}

if (!customElements.get('ww-form-steps')) {
  class WWFormSteps extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.steps = Array.from(this.querySelectorAll('.ww-form__step'));
      if (!this.form || this.steps.length === 0) return;

      this.prevButton = this.querySelector('[data-step-prev]');
      this.nextButton = this.querySelector('[data-step-next]');
      this.submitButton = this.querySelector('[data-step-submit]');
      this.segments = Array.from(this.querySelectorAll('.ww-form__progress-seg'));
      this.stepLabel = this.querySelector('.ww-form__step-label');
      this.errorBox = this.querySelector('[data-form-error]');
      if (this.errorBox && !this.errorBox.id) this.errorBox.id = `${this.form.id || 'ww-form'}-error`;
      this.panel = this.closest('.ww-modal__panel');
      this.modal = this.closest('ww-apply-modal');

      // Hidden steps still contain required fields. The browser's own
      // pre-submit check cannot show a bubble on a hidden control, so it is
      // switched off and each step is validated by hand instead; problems are
      // written into the inline error box rather than a native bubble.
      this.form.noValidate = true;

      this.addEventListener('click', this.onClick.bind(this));
      this.form.addEventListener('keydown', this.onKeydown.bind(this));
      this.form.addEventListener('input', this.onInput.bind(this));
      this.form.addEventListener('change', this.onChange.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = this.onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      // After a server-side error Shopify re-renders the page; start on the
      // step that holds the invalid field and keep the server's message.
      const invalid = this.form.querySelector('[aria-invalid="true"]');
      const start = invalid ? this.steps.indexOf(invalid.closest('.ww-form__step')) : 0;
      this.show(Math.max(start, 0), false, true);
    }

    disconnectedCallback() {
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onClick(event) {
      if (event.target.closest('[data-step-next]')) {
        event.preventDefault();
        this.next();
      } else if (event.target.closest('[data-step-prev]')) {
        event.preventDefault();
        this.prev();
      } else if (event.target.closest('[data-step-submit]')) {
        // Validate on the click, before a submit event exists: Shopify's
        // storefront captcha listens for submit on contact forms, shows its
        // challenge and then resubmits the form itself, which would skip the
        // checks in onSubmit. Enter in a field also routes through this click.
        if (!this.validateAll()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
    }

    onKeydown(event) {
      // Enter in a text input would submit the whole form early.
      if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;
      if (this.current < this.steps.length - 1) {
        event.preventDefault();
        this.next();
      }
    }

    onInput(event) {
      this.clearError(event.target);
    }

    onChange(event) {
      const group = event.target.closest('[data-required-group]');
      if (group) this.syncGroup(group);
      this.clearError(event.target);
    }

    onSubmit(event) {
      if (!this.validateAll()) {
        event.preventDefault();
        return;
      }
      this.notifyWebhook();
    }

    validateAll() {
      return !this.steps.some((step) => !this.validate(step));
    }

    onBlockSelect(event) {
      const target = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      const step = target && target.closest('.ww-form__step');
      if (step) this.show(this.steps.indexOf(step), false);
    }

    // A "require at least one" checkbox group has no native rule, so the first
    // box carries a custom validity flag until any box is ticked.
    syncGroup(group) {
      const boxes = Array.from(group.querySelectorAll('input[type="checkbox"]'));
      if (boxes.length === 0) return;
      const anyChecked = boxes.some((box) => box.checked);
      boxes[0].setCustomValidity(anyChecked ? '' : 'required');
    }

    validate(step) {
      step.querySelectorAll('[data-required-group]').forEach((group) => this.syncGroup(group));
      const fields = Array.from(step.querySelectorAll('input, select, textarea'));
      const invalid = fields.find((field) => !field.checkValidity());
      if (!invalid) return true;
      if (this.steps.indexOf(step) !== this.current) this.show(this.steps.indexOf(step), false);
      this.showError(this.messageFor(invalid), invalid);
      return false;
    }

    // Builds the message from the section's templates and the field's label,
    // e.g. "Please add your company name."
    messageFor(field) {
      const wrapper = field.closest('.ww-form__field');
      const rawLabel = (wrapper && wrapper.dataset.label) || '';
      const label = rawLabel.charAt(0).toLowerCase() + rawLabel.slice(1);
      const messages = this.dataset;
      let template;

      if (wrapper && wrapper.hasAttribute('data-agreement')) {
        template = messages.msgAgree || 'Please agree to the terms to submit.';
      } else if (wrapper && wrapper.hasAttribute('data-required-group')) {
        template = messages.msgGroup || 'Please pick at least one option.';
      } else if (wrapper && wrapper.hasAttribute('data-choice-group')) {
        template = messages.msgChoice || 'Please pick your {label}.';
      } else if (field.validity.valueMissing) {
        template = messages.msgRequired || 'Please add your {label}.';
      } else {
        template = messages.msgInvalid || 'Please enter a valid {label}.';
      }
      return template.replace('{label}', label);
    }

    showError(text, field) {
      if (this.errorBox) this.errorBox.textContent = text;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        // Tie the message to the field so it is read again on refocus.
        if (this.errorBox) this.setDescribedBy(field, this.errorBox.id, true);
        field.focus();
      }
    }

    clearError(field) {
      if (field && field.removeAttribute) field.removeAttribute('aria-invalid');
      if (this.errorBox && this.errorBox.textContent !== '') this.errorBox.textContent = '';
      if (this.errorBox && this.errorBox.id) {
        this.form.querySelectorAll(`[aria-describedby~="${this.errorBox.id}"]`).forEach((element) => {
          this.setDescribedBy(element, this.errorBox.id, false);
        });
      }
    }

    // Adds or removes one id in an element's aria-describedby list.
    setDescribedBy(element, id, add) {
      const ids = (element.getAttribute('aria-describedby') || '').split(/\s+/).filter((item) => item && item !== id);
      if (add) ids.push(id);
      if (ids.length) element.setAttribute('aria-describedby', ids.join(' '));
      else element.removeAttribute('aria-describedby');
    }

    // a11y: re-arm the theme's focus trap so its first/last controls reflect
    // the step now showing (ModalDialog captured them when the modal opened).
    refreshTrap(target) {
      if (!this.modal || !this.modal.hasAttribute('open') || typeof trapFocus !== 'function') return;
      trapFocus(this.modal, target);
    }

    next() {
      if (!this.validate(this.steps[this.current])) return;
      this.show(this.current + 1);
    }

    prev() {
      this.show(this.current - 1);
    }

    show(index, focus = true, keepError = false) {
      this.current = Math.min(Math.max(index, 0), this.steps.length - 1);
      const total = this.steps.length;
      const number = this.current + 1;
      const isLast = number === total;
      const step = this.steps[this.current];

      this.steps.forEach((item, i) => {
        item.hidden = i !== this.current;
      });
      if (this.prevButton) this.prevButton.hidden = this.current === 0;
      if (this.nextButton) this.nextButton.hidden = isLast;
      if (this.submitButton) this.submitButton.hidden = !isLast;
      this.segments.forEach((segment, i) => segment.classList.toggle('is-done', i <= this.current));

      if (this.stepLabel) {
        const label = (this.dataset.stepLabel || 'Step {current} of {total}')
          .replace('{current}', number)
          .replace('{total}', total);
        const name = step.dataset.stepName;
        this.stepLabel.textContent = name ? `${label} · ${name}` : label;
      }

      if (!keepError) this.clearError();
      if (this.panel) this.panel.scrollTop = 0;
      if (focus) {
        // Steps without a title focus the fieldset itself (tabindex="-1").
        const title = step.querySelector('.ww-form__step-title');
        const target = title || step;
        target.focus({ preventScroll: true });
        this.refreshTrap(target);
      }
    }

    // Best-effort mirror of the submission for an extra recipient, in the
    // shape the form-to-email webhook expects: { to, name, email, message,
    // site, honeypot }. Every field is also sent under `fields` for other
    // receivers. keepalive lets the request finish after the native submit
    // navigates away.
    notifyWebhook() {
      const url = this.dataset.webhookUrl;
      if (!url || typeof window.fetch !== 'function') return;

      // The reply address posts as contact[email]; label it like the others.
      const emailField = this.form.querySelector('[name="contact[email]"]');
      const emailWrapper = emailField && emailField.closest('.ww-form__field');
      const emailLabel = (emailWrapper && emailWrapper.dataset.label) || 'Email';

      const fields = {};
      new FormData(this.form).forEach((value, key) => {
        const match = key.match(/^contact\[(.+)\]$/);
        if (!match || String(value).trim() === '') return;
        fields[match[1] === 'email' ? emailLabel : match[1]] = value;
      });

      const valueOf = (selector) => {
        const field = this.form.querySelector(selector);
        return field ? field.value.trim() : '';
      };
      const fullName = [valueOf('[autocomplete="given-name"]'), valueOf('[autocomplete="family-name"]')]
        .filter(Boolean)
        .join(' ');
      const honeypot = this.form.querySelector('[data-honeypot]');

      const payload = {
        name: fullName || valueOf('[autocomplete="name"]'),
        email: valueOf('[name="contact[email]"]'),
        message: Object.keys(fields).map((label) => `${label}: ${fields[label]}`).join('\n'),
        site: window.location.hostname,
        honeypot: honeypot ? honeypot.value : '',
        fields,
      };
      if (this.dataset.webhookTo) payload.to = this.dataset.webhookTo;
      // Names the email: "New {title} form submission".
      if (this.dataset.webhookTitle) payload.title = this.dataset.webhookTitle;

      try {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch (error) {
        // The Shopify email is the source of truth; nothing to recover here.
      }
    }
  }

  customElements.define('ww-form-steps', WWFormSteps);
}
